import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, Loader2, Clock, Backpack, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatISK, formatDate, formatApiErrorDetail } from "@/lib/api";
import CancellationPolicy from "@/components/CancellationPolicy";
import PhoneInput from "@/components/PhoneInput";

function Counter({ label, sub, value, onChange, min = 0, testid }) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-white/10">
      <div>
        <p className="text-sm uppercase tracking-[0.1em]">{label}</p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
      <div className="flex items-center gap-5">
        <button
          type="button"
          data-testid={`${testid}-minus`}
          onClick={() => onChange(Math.max(min, value - 1))}
          className="w-9 h-9 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30"
          disabled={value <= min}
        >
          <Minus size={15} />
        </button>
        <span data-testid={`${testid}-value`} className="w-6 text-center font-display font-bold text-lg">{value}</span>
        <button
          type="button"
          data-testid={`${testid}-plus`}
          onClick={() => onChange(value + 1)}
          className="w-9 h-9 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors"
        >
          <Plus size={15} />
        </button>
      </div>
    </div>
  );
}

export default function TourBooking() {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [dates, setDates] = useState([]);
  const [closedDates, setClosedDates] = useState([]);
  const [date, setDate] = useState("");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [customer, setCustomer] = useState({ full_name: "", email: "", phone: "", allergies: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [feePercent, setFeePercent] = useState(0);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    api.get(`/tours/${tourId}`).then((res) => {
      setTour(res.data.tour);
      setDates(res.data.dates);
      setClosedDates(res.data.tours_closed_dates || []);
    }).catch(() => navigate("/tours"));
    api.get("/config").then((res) => setFeePercent(res.data.card_fee_percent || 0)).catch(() => {});
  }, [tourId, navigate]);

  if (!tour) {
    return <div className="min-h-[60vh] flex items-center justify-center text-gray-500 text-xs uppercase tracking-[0.3em]">Loading…</div>;
  }

  if (tour.coming_soon) {
    return (
      <div data-testid="tour-coming-soon-page" className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">{tour.name}</p>
        <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.95]">Coming Soon</h1>
        <p className="text-gray-400 mt-6 leading-relaxed max-w-lg mx-auto">
          {tour.name} isn't available to book just yet — we're finalising the details. Please check back soon or explore our other experiences.
        </p>
        <Link to="/tours" className="mt-10 inline-flex items-center gap-3 bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 transition-colors">
          <ArrowLeft size={15} /> Back to Tours
        </Link>
      </div>
    );
  }

  const childPrice = tour.child_price || 0;
  const total = adults * tour.adult_price + children * childPrice;
  const cardFee = Math.round(total * feePercent / 100);
  const grandTotal = total + cardFee;
  const guests = adults + children;

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Please choose a date");
    if (guests < 1) return toast.error("Add at least one guest");
    if (!customer.full_name || !customer.email || !customer.phone) return toast.error("Please complete your details");
    if (!agreed) return toast.error("Please confirm you understand the booking notice");
    setSubmitting(true);
    try {
      const res = await api.post("/bookings/tour", {
        tour_id: tour.id, date, adults, children, customer,
      });
      const checkout = await api.post("/payments/checkout", {
        booking_id: res.data.booking.id, origin_url: window.location.origin,
      });
      window.location.href = checkout.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="tour-booking-page">
      <div className="relative h-[40vh] min-h-[300px] overflow-hidden">
        <img src={tour.image} alt={tour.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-10">
          <Link to="/tours" className="text-xs uppercase tracking-[0.2em] text-gray-300 hover:text-white inline-flex items-center gap-2 mb-5"><ArrowLeft size={14} /> All tours</Link>
          <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.92] max-w-3xl">{tour.name}</h1>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          {/* About */}
          <section data-testid="tour-about">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5">About This Experience</p>
            <p className="text-gray-300 leading-relaxed max-w-2xl">{tour.description}</p>
            {(tour.bring || tour.includes) && (
              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                {tour.bring && (
                  <div data-testid="tour-bring" className="border border-white/10 bg-[#0C0C0E] p-5">
                    <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-2 inline-flex items-center gap-2"><Backpack size={15} /> What to bring</p>
                    <p className="text-sm text-gray-300 leading-relaxed">{tour.bring}</p>
                  </div>
                )}
                {tour.includes && (
                  <div data-testid="tour-includes" className="border border-white/10 bg-[#0C0C0E] p-5">
                    <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-2 inline-flex items-center gap-2"><Check size={15} /> What's included</p>
                    <p className="text-sm text-gray-300 leading-relaxed">{tour.includes}</p>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Date */}
          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5">01 — Select Date</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {dates.map((d) => {
                const closed = closedDates.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    data-testid={`date-${d}`}
                    onClick={() => !closed && setDate(d)}
                    disabled={closed}
                    title={closed ? "Guided tours are fully allocated on this day" : undefined}
                    className={`py-4 px-3 border text-center transition-all ${closed ? "border-white/10 text-gray-600 cursor-not-allowed line-through decoration-gray-600" : date === d ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/50"}`}
                  >
                    <span className="block text-xs uppercase tracking-[0.1em]">{formatDate(d)}</span>
                    {closed && <span className="block text-[9px] uppercase tracking-[0.08em] text-amber-500/80 mt-1 no-underline">Fully allocated</span>}
                  </button>
                );
              })}
            </div>
            {tour.departure_time && (
              <p className="mt-4 inline-flex items-center gap-2 text-sm uppercase tracking-[0.12em] text-white"><Clock size={15} /> Departs {tour.departure_time} from Viking Café</p>
            )}
            <p className="mt-4 text-[11px] leading-relaxed text-gray-500 italic max-w-xl">
              Estimate departure time. Exact departure times will be confirmed closer to the event and may change due to weather or operational conditions.
            </p>
          </section>

          {/* Guests */}
          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">02 — Guests</p>
            <Counter label="Guests" sub={formatISK(tour.adult_price)} value={adults} onChange={setAdults} min={0} testid="adults" />
            {tour.has_child && (
              <Counter label="Children" sub={`${formatISK(tour.child_price)} · age ${tour.child_age}`} value={children} onChange={setChildren} min={0} testid="children" />
            )}
          </section>

          {/* Customer */}
          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-6">03 — Your Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Full name</label>
                <input data-testid="customer-name" required value={customer.full_name} onChange={(e) => setCustomer({ ...customer, full_name: e.target.value })} className="bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-white transition-colors w-full" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Email</label>
                <input type="email" data-testid="customer-email" required value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-white transition-colors w-full" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Phone</label>
                <PhoneInput testid="customer-phone" required onChange={(v) => setCustomer((prev) => ({ ...prev, phone: v }))} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Notes (optional)</label>
                <input data-testid="customer-notes" value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} className="bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-white transition-colors w-full" />
              </div>
            </div>
          </section>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:sticky lg:top-28 bg-[#0C0C0E] border border-white/10 p-8">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-6">Summary</p>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-gray-400"><span>Date</span><span className="text-white">{date ? formatDate(date) : "—"}</span></div>
              <div className="flex justify-between text-gray-400"><span>Guests × {adults}</span><span className="text-white">{formatISK(adults * tour.adult_price)}</span></div>
              {tour.has_child && children > 0 && (
                <div className="flex justify-between text-gray-400"><span>Children × {children}</span><span className="text-white">{formatISK(children * childPrice)}</span></div>
              )}
            </div>
            <div className="mt-6 pt-6 border-t border-white/10">
              <span className="text-xs uppercase tracking-[0.15em] text-gray-400 block">Total</span>
              <span data-testid="tour-total" className="font-display font-extrabold text-3xl block mt-1">{formatISK(grandTotal)}</span>
            </div>
            {cardFee > 0 && (
              <div className="flex justify-between text-[11px] text-gray-500 mt-3">
                <span data-testid="tour-fee-label">Incl. {feePercent}% card fee</span>
                <span data-testid="tour-fee-amount">{formatISK(cardFee)}</span>
              </div>
            )}
            <div data-testid="tour-transfer-disclaimer" className="mt-6 border border-amber-500/30 bg-amber-500/[0.06] p-4">
              <p className="text-[10px] uppercase tracking-[0.15em] text-amber-400 font-bold mb-2 inline-flex items-center gap-2"><AlertTriangle size={13} /> Please note before you book</p>
              <p className="text-[12px] leading-relaxed text-gray-300">
                Our festival activity concierge must allocate every guest into transfers and confirm availability. Your tour time could be <span className="text-white">moved</span>, or in rare cases <span className="text-white">cancelled</span> — if we cancel your tour you'll receive a <span className="text-white">full refund</span>.
              </p>
              <p className="text-[12px] leading-relaxed text-gray-300 mt-2">
                If you arrive <span className="text-white">late for your transfer</span> you may miss your tour, and late arrivals are <span className="text-white">not guaranteed a refund</span>.
              </p>
              <label className="flex items-start gap-3 mt-4 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="tour-disclaimer-agree"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-amber-400 shrink-0 cursor-pointer"
                />
                <span className="text-[12px] leading-relaxed text-gray-200">I have read and understand this notice, and agree to it.</span>
              </label>
            </div>
            <button
              type="submit"
              data-testid="confirm-tour-booking"
              disabled={submitting || !agreed || tour?.cancelled}
              title={tour?.cancelled ? "This tour has been cancelled" : (!agreed ? "Please tick the box above to continue" : undefined)}
              className="w-full mt-6 bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 transition-colors inline-flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Redirecting to payment</> : tour?.cancelled ? "Tour cancelled" : "Pay & Confirm"}
            </button>
          </motion.div>
          <CancellationPolicy className="mt-6" />
        </div>
      </form>
    </div>
  );
}
